// « S'exercer » (DRILL) — onglet « Historique des exercices ». Arbo simulateur → critère → sessions,
// détail en 3 onglets (Synthèse du coach / Transcription / Capsules). Cadrage §11.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, ChevronDown, ChevronRight, Calendar, Clock, FileText, MessageCircle, GraduationCap, Check } from "lucide-react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { drillSessionApi } from "../lib/api";
import { usePilotScope } from "../lib/pilot-scope";

const NAVY = "#2E4368";

/**
 * `team` : historique de l'ÉQUIPE dans Pilot (décision 2.3) — exercices des personnes du périmètre
 * de la barre de Pilot, avec leur nom ; `userId` réduit à une personne.
 * Sans `team`, `userId` : Play › Historique du RH (« Tous les collaborateurs » ou une personne de
 * son compte, décision D4 du 30/09/2026), avec le nom de chacun.
 */
export function DrillHistoryView({ team, userId }: { team?: boolean; userId?: string } = {}) {
  const { t, i18n } = useTranslation();
  const [sessions, setSessions] = useState<any[] | null>(null);
  const [openSim, setOpenSim] = useState<string | null>(null);
  const [openCrit, setOpenCrit] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailTab, setDetailTab] = useState<"synthese" | "transcript" | "capsules">("synthese");
  const [loadingDetail, setLoadingDetail] = useState(false);

  const pilot = usePilotScope();
  /** Un échec se DIT (audit du lot 5) : il s'affichait comme « aucun exercice », ou en chargement sans fin. */
  const [listError, setListError] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    setSessions(null);
    setSelectedId(null);
    setListError(false);
    drillSessionApi.history(team ? { team: true, userId } : userId ? { userId } : undefined)
      .then(setSessions)
      .catch(() => { setSessions([]); setListError(true); });
  }, [team, userId, team ? pilot.account : null, team ? pilot.scope : null, reload]);

  useEffect(() => {
    setDetailError(false);
    if (!selectedId) { setDetail(null); return; }
    setLoadingDetail(true);
    drillSessionApi.detail(selectedId)
      .then((d) => { setDetail(d); if (!d) setDetailError(true); })
      .catch(() => { setDetail(null); setDetailError(true); })
      .finally(() => setLoadingDetail(false));
  }, [selectedId]);

  // Regroupement simulateur → critère → sessions.
  const tree = useMemo(() => {
    const bySim = new Map<string, { simLabel: string; criteria: Map<string, { critLabel: string; sessions: any[] }> }>();
    for (const s of sessions ?? []) {
      const simKey = s.simulatorLabel ?? "—";
      if (!bySim.has(simKey)) bySim.set(simKey, { simLabel: simKey, criteria: new Map() });
      const crit = bySim.get(simKey)!.criteria;
      if (!crit.has(s.criterionKey)) crit.set(s.criterionKey, { critLabel: s.criterionLabel ?? s.criterionKey, sessions: [] });
      crit.get(s.criterionKey)!.sessions.push(s);
    }
    return [...bySim.entries()];
  }, [sessions]);

  const fmtDate = (d: string) => new Date(d).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" });
  const fmtDur = (s?: number | null) => (s == null ? "—" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);

  if (sessions === null) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (listError) return (
    <div role="alert" className="flex flex-wrap items-center gap-3 px-1 py-6 text-sm" style={{ color: "#C0423F" }}>
      {t("common.loadError")}
      <button type="button" className="font-semibold underline" onClick={() => setReload((n) => n + 1)}>{t("common.retry")}</button>
    </div>
  );
  if (sessions.length === 0) return <p className="px-1 py-6 text-sm text-slate-400">{t("drill.histEmpty")}</p>;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-5" style={{ color: NAVY }}>
      {/* Arbre */}
      <div className="space-y-2 lg:col-span-2">
        {tree.map(([simKey, sim]) => (
          <Card key={simKey} className="overflow-hidden">
            <button onClick={() => setOpenSim(openSim === simKey ? null : simKey)} className="flex w-full items-center justify-between px-4 py-3 text-left">
              <span className="font-semibold">{sim.simLabel}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">{[...sim.criteria.values()].reduce((n, c) => n + c.sessions.length, 0)} {t("drill.histSessions")}</span>
                {openSim === simKey ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
              </div>
            </button>
            {openSim === simKey && (
              <div className="border-t">
                {[...sim.criteria.entries()].map(([critKey, crit]) => (
                  <div key={critKey} className="border-b last:border-0">
                    <button onClick={() => setOpenCrit(openCrit === critKey ? null : critKey)} className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-slate-50">
                      <span>{crit.critLabel}</span>
                      <Badge variant="secondary">{crit.sessions.length}</Badge>
                    </button>
                    {openCrit === critKey && crit.sessions.map((s) => (
                      <button key={s.id} onClick={() => setSelectedId(s.id)} className="flex w-full items-center justify-between px-4 py-2 pl-8 text-left text-sm" style={selectedId === s.id ? { backgroundColor: "#EDF2F9" } : {}}>
                        <div>
                          <div className={selectedId === s.id ? "font-medium" : ""}>{(team || userId) && s.userName ? `${s.userName} · ` : ""}{fmtDate(s.createdAt)}</div>
                          <div className="text-xs text-slate-400">{fmtDur(s.durationSeconds)} · {s.roundsCompleted ?? 0} {t("drill.histRounds")}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Détail */}
      <div className="lg:col-span-3">
        {!selectedId ? (
          <Card className="flex items-center justify-center py-16 text-sm text-slate-400">{t("drill.histSelect")}</Card>
        ) : detailError && !loadingDetail ? (
          <Card role="alert" className="flex items-center justify-center py-16 text-sm" style={{ color: "#C0423F" }}>{t("drill.histDetailError")}</Card>
        ) : loadingDetail || !detail ? (
          <Card className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></Card>
        ) : (
          <Card className="p-5">
            <div className="mb-3">
              <div className="text-lg font-bold">{detail.session.criterionLabel}</div>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtDate(detail.session.createdAt)}</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDur(detail.session.durationSeconds)}</span>
                <span>{detail.session.roundsCompleted ?? 0} {t("drill.histRounds")}</span>
                <Badge variant="secondary">{detail.session.simulatorLabel}</Badge>
              </div>
            </div>

            <div className="mb-4 flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1 text-sm">
              {([["synthese", t("drill.histTabSummary"), FileText], ["transcript", t("drill.histTabTranscript"), MessageCircle], ["capsules", t("drill.histTabCapsules"), GraduationCap]] as const).map(([k, label, Icon]) => (
                <button key={k} onClick={() => setDetailTab(k)} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 ${detailTab === k ? "bg-white font-medium shadow-sm" : "text-slate-500"}`}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>

            {detailTab === "synthese" && (
              detail.session.summaryText
                ? <div className="whitespace-pre-wrap rounded-2xl bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">{detail.session.summaryText}</div>
                : <p className="text-sm text-slate-400">{t("drill.histNoSummary")}</p>
            )}
            {detailTab === "transcript" && (
              <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
                {detail.transcript.length === 0 && <p className="text-sm text-slate-400">{t("drill.histNoTranscript")}</p>}
                {detail.transcript.map((m: any, i: number) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-md rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-sm text-white" : "rounded-bl-sm"}`} style={m.role === "user" ? { backgroundColor: "#2C4A6E" } : { backgroundColor: "#F7F8FA", color: NAVY }}>{m.content}</div>
                  </div>
                ))}
              </div>
            )}
            {detailTab === "capsules" && (
              <div className="space-y-2">
                {detail.capsules.length === 0 && <p className="text-sm text-slate-400">{t("drill.histNoCapsules")}</p>}
                {detail.capsules.map((c: any) => (
                  <div key={c.id} className="flex items-center justify-between rounded-2xl border px-4 py-3">
                    <span className="text-sm font-medium">{c.title ?? c.titleFr}</span>
                    {c.status === "completed"
                      ? <Badge variant="secondary"><Check className="mr-1 h-3 w-3" /> {t("drill.doneTag")}</Badge>
                      : <Badge variant="outline" className="text-slate-500">{t("drill.toDiscover")}</Badge>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
